// Renderer-independent marker artwork. Map.svelte uses these SVG data URLs in
// ordinary DOM markers, keeping category styling out of the map engine.

export const PLACE_TYPES = Object.freeze({
	FOOD: 0,
	SITE: 1,
	SHOPPING: 2,
	HOME: 3,
	OTHER: 4,
	NONE: -11111
});

export const createSvgIcon = (innerPath = '', fillColor = '#4285F4', strokeColor = '#FFFFFF') => `
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 44" width="32" height="44">
    <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
      <feDropShadow dx="0" dy="2" stdDeviation="2" flood-color="#000000" flood-opacity="0.3"/>
    </filter>
    <path
      d="M16 2.5c-7.2 0-13 5.8-13 13 0 2.4 0.6 4.7 1.9 6.6l11.1 19.9 11.1-19.9c1.3-1.9 1.9-4.2 1.9-6.6 0-7.2-5.8-13-13-13z"
      fill="${fillColor}"
      stroke="${strokeColor}"
      stroke-width="1"
      filter="url(#shadow)"
    />
    <circle cx="16" cy="15.5" r="8" fill="${strokeColor}"/>
    <g transform="translate(8.5, 8)">
      ${innerPath}
    </g>
  </svg>
`;

const ICON_CONFIGS = {
	FOOD: {
		color: '#DB4437',
		path: `<path fill="#DB4437" d="M7.5 12c-2.5 0-4.5-2-4.5-4.5S5 3 7.5 3 12 5 12 7.5 10 12 7.5 12zm-1-2h2v-2h2v-2h-2v-2h-2v2h-2v2h2z"/>`
	},
	SITE: {
		color: '#0F9D58',
		path: `<path fill="#0F9D58" d="M12 7.5c0-2.5-2-4.5-4.5-4.5S3 5 3 7.5 5 12 7.5 12 12 10 12 7.5zm-6 0l1.5-1.5L9 7.5 7.5 9 6 7.5z"/>`
	},
	SHOPPING: {
		color: '#4285F4',
		path: `<path fill="#4285F4" d="M11 7.5c0-2.5-2-4.5-4.5-4.5S2 5 2 7.5 4 12 6.5 12 11 10 11 7.5zm-5 2l-2-2 1-1 1 1 2-2 1 1-3 3z"/>`
	},
	HOME: {
		color: '#F4B400',
		path: `<path fill="#F4B400" d="M7.5 3l-4.5 4.5h1.5v4.5h6v-4.5h1.5z"/>`
	},
	OTHER: {
		color: '#757575',
		path: `<path fill="#757575" d="M7.5 3c-2.5 0-4.5 2-4.5 4.5s2 4.5 4.5 4.5 4.5-2 4.5-4.5-2-4.5-4.5-4.5zm0 7.5c-.8 0-1.5-.7-1.5-1.5s.7-1.5 1.5-1.5 1.5.7 1.5 1.5-.7 1.5-1.5 1.5z"/>`
	},
	NONE: { color: '#757575', path: '' }
};

const SVG_STRINGS = Object.freeze(
	Object.fromEntries(
		Object.entries(ICON_CONFIGS).map(([key, config]) => [key, createSvgIcon(config.path, config.color)])
	)
);

// Encode SVG as a data URL (no blob URL to leak per icon per page load).
/** @param {string} svg */
const svgToDataUrl = (svg) => `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;

export const MARKER_ICON_URLS = Object.freeze(
	Object.fromEntries(Object.entries(SVG_STRINGS).map(([key, svg]) => [key, svgToDataUrl(svg)]))
);

/** @type {Readonly<Record<number, string>>} */
const TYPE_TO_KEY = Object.freeze({
	[PLACE_TYPES.FOOD]: 'FOOD',
	[PLACE_TYPES.SITE]: 'SITE',
	[PLACE_TYPES.SHOPPING]: 'SHOPPING',
	[PLACE_TYPES.HOME]: 'HOME',
	[PLACE_TYPES.OTHER]: 'OTHER'
});

/** Map a place `type` number to a marker-icon key (defaults to OTHER). */
/** @param {number} type */
export function iconKeyForType(type) {
	return TYPE_TO_KEY[type] ?? 'OTHER';
}

// Category brand colours (single source of truth; mirrors ICON_CONFIGS). Used by
// the saved-places list type tags so the component carries no colour literals.
export const TYPE_COLORS = Object.freeze({
	[PLACE_TYPES.FOOD]: ICON_CONFIGS.FOOD.color,
	[PLACE_TYPES.SITE]: ICON_CONFIGS.SITE.color,
	[PLACE_TYPES.SHOPPING]: ICON_CONFIGS.SHOPPING.color,
	[PLACE_TYPES.HOME]: ICON_CONFIGS.HOME.color,
	[PLACE_TYPES.OTHER]: ICON_CONFIGS.OTHER.color
});

// Wire values match Android LocationCustomMarkerData exactly. Older web builds
// persisted string values; normalizeCustomMarkerData upgrades those values at
// every read/write boundary without changing the visible colour.
export const CUSTOM_MARKER_TYPE_CIRCLE = 11;
/** @type {Readonly<Record<string, number>>} */
export const MARKER_COLOR_IDS = Object.freeze({
	blue: 1,
	green: 2,
	orange: 3,
	pink: 4,
	red: 5,
	yellow: 6
});
export const MARKER_COLOR_KEYS = Object.freeze(['blue', 'green', 'orange', 'pink', 'red', 'yellow']);
/** @type {Readonly<Record<number, string>>} */
export const MARKER_COLORS = Object.freeze({
	[MARKER_COLOR_IDS.blue]: '#2563EB',
	[MARKER_COLOR_IDS.green]: '#22C55E',
	[MARKER_COLOR_IDS.orange]: '#F59E0B',
	[MARKER_COLOR_IDS.pink]: '#EC4899',
	[MARKER_COLOR_IDS.red]: '#EF4444',
	[MARKER_COLOR_IDS.yellow]: '#EAB308'
});

/** @param {number} colorType */
export function markerColorKey(colorType) {
	return MARKER_COLOR_KEYS.find((key) => MARKER_COLOR_IDS[key] === colorType) || null;
}

/** @param {any} value */
export function normalizeCustomMarkerData(value) {
	if (!value || typeof value !== 'object') return undefined;
	const type = value.type === 'circle' ? CUSTOM_MARKER_TYPE_CIRCLE : value.type;
	const colorType = typeof value.colorType === 'string'
		? MARKER_COLOR_IDS[value.colorType]
		: value.colorType;
	if (type !== CUSTOM_MARKER_TYPE_CIRCLE || !MARKER_COLORS[colorType]) return undefined;
	return { type: CUSTOM_MARKER_TYPE_CIRCLE, colorType };
}
